import { useMutation } from '@apollo/client'
import { message, Modal, Spin } from 'antd'
import { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react'
import { MyBtn } from '~/components/ui/MyBtn'
import { POLL_SUPER_AI_ARTWORK, SUPER_SIZE_AI_ARTWORK } from '~/graphqls/gqls'
import {
  getAIArtWorkNewst36_pictureGraph_aiArtworkPage_edges_node,
  getAIArtWork_pictureGraph_aiArtwork,
  pollSuperAiArtwork,
  pollSuperAiArtworkVariables,
  superSizeAiArtwork,
  superSizeAiArtworkVariables,
  SuperSizeStatus,
} from '~/graphqls/types'
import { useImperativeQuery } from '~/hooks/useImperativeQuery'
import { graphQLErrorMessage, xhrDownload } from '~/utils'
import { LoadingOutlined } from '@ant-design/icons'
import { useYellow } from './useYellow'

export const useHR = ({
  product,
  setProduct,
  supersizeTimes,
}: {
  product:
    | Omit<
        getAIArtWorkNewst36_pictureGraph_aiArtworkPage_edges_node,
        '__typename'
      >
    | getAIArtWork_pictureGraph_aiArtwork
  setProduct: Dispatch<SetStateAction<any>>
  supersizeTimes: number
}) => {
  const [hrOpen, setHrOpen] = useState(false)
  const [previewVisible, setPreviewVisible] = useState(false)
  const [previewImage, setPreviewImage] = useState('')

  const [superSizeAction] = useMutation<
    superSizeAiArtwork,
    superSizeAiArtworkVariables
  >(SUPER_SIZE_AI_ARTWORK)

  const pollSuperSizeInfo = useImperativeQuery<
    pollSuperAiArtwork,
    pollSuperAiArtworkVariables
  >(POLL_SUPER_AI_ARTWORK)

  /** 鉴黄逻辑 */
  const { validateYellow } = useYellow(!!product?.involveYellow)

  const superSizeRef = useRef(0)
  const updateSuperSizeInfo = () => {
    if (!product) return

    pollSuperSizeInfo({ id: product.id })
      .then(res => {
        const status = res.data.pictureGraph?.aiArtwork?.superSizePicInfo?.map(
          item => item!.status
        )
        if (status?.includes(SuperSizeStatus.SUPERING)) {
          superSizeRef.current = window.setTimeout(() => {
            updateSuperSizeInfo()
          }, 2000)
        } else {
          window.clearTimeout(superSizeRef.current)
        }

        setProduct((product: any) => {
          return {
            ...product,
            superSizePicInfo:
              res?.data?.pictureGraph?.aiArtwork?.superSizePicInfo?.map(
                item => {
                  return { ...item }
                }
              ) as any[],
          }
        })
      })
      .catch(() => {
        superSizeRef.current = window.setTimeout(() => {
          updateSuperSizeInfo()
        }, 2000)
      })
  }

  const handleSuperSize = (multiple: number, points: number = 0) => {
    if (!product) return
    let tempParam = {}
    if (!!supersizeTimes) {
      tempParam = {
        vipRightsUse: {
          superSizeTimesUse: 1,
        },
      }
    } else {
      tempParam = {
        integralUse: {
          integralUseOnSuperSize: points,
        },
      }
    }

    return superSizeAction({
      variables: {
        id: product.id,
        multiple: 0, // 旧参数默认0，后面废弃
        multipleNew: multiple,
        payStrategy: { ...tempParam },
      },
    }).catch(err => {
      message.destroy()
      message.error(graphQLErrorMessage(err) || '请重试')
    })
  }

  const validateSuperSize = () => {
    if (!product) return
    if (!validateYellow()) return
    if (!product.superSizePicInfo?.filter(it => it?.multiple !== 1)?.length) {
      Modal.info({
        centered: true,
        title: '提示',
        maskStyle: {
          backgroundColor: 'rgba(0,0,0,0.8)',
        },
        width: 270,
        className: 'hookModal dark',
        content: (
          <div className="text-neutral-400 text-sm text-left -mb-2">
            图片尺寸过大，无法再次进行超分辨
          </div>
        ),
        okText: '知道了',
        onOk: close => {
          close()
        },
      })
      setHrOpen(false)
    } else {
      setHrOpen(true)
    }
  }

  const HRDropdown = (
    handleSuperSize: (multiple: number, points?: number) => void
  ) => {
    return (
      <div className="bg-neutral-200 rounded-lg text-center py-2 px-4 font-semibold text-neutral-400 shadow-[0_4px_16px_-8px_rgba(15,15,15,0.2)]">
        {product.superSizePicInfo
          ?.filter(item => {
            return item?.multiple !== 1
          })
          .map((item, index) => {
            return (
              <div key={index}>
                {index !== 0 && (
                  <div className="w-full h-px bg-neutral-300 my-2"></div>
                )}
                <div
                  className="py-2 flex items-center justify-between"
                  key={item?.multiple}
                >
                  <span>
                    <span>{`${item?.length}*${item?.width}`}</span>
                  </span>

                  {item?.status === SuperSizeStatus.UNSUPER && (
                    <div>
                      {!!supersizeTimes ? (
                        <div
                          onClick={() => handleSuperSize(item?.multiple)}
                          className="rounded-full py-1.5 px-3 text-neutral-50 text-[12px] leading-none ml-4 cursor-pointer whitespace-nowrap "
                          style={{
                            background: '#f9deb9', // 备注：不使用 bg-[#f9deb9] 是因为Popover组件是异步加载的，而tallwind自定义样式需要在一开始就生成好CSS类
                          }}
                        >
                          使用权益卡
                        </div>
                      ) : (
                        <MyBtn
                          type="primary"
                          size="small"
                          className="ml-4"
                          onClick={() =>
                            handleSuperSize(item?.multiple, item?.integral)
                          }
                        >
                          {item.integral}积分
                        </MyBtn>
                      )}
                    </div>
                  )}
                  {item?.status === SuperSizeStatus.SUPERING && (
                    <span className="ml-4">
                      <Spin
                        indicator={
                          <LoadingOutlined style={{ fontSize: 16 }} spin />
                        }
                      />
                    </span>
                  )}
                  {item?.status === SuperSizeStatus.SUCCESS && (
                    <span className="flex">
                      <MyBtn
                        $dark={true}
                        size="small"
                        className="ml-4 !border-2"
                        onClick={() => {
                          setPreviewVisible(true)
                          setPreviewImage(item.artWorkUrl!)
                        }}
                      >
                        <span className="text-neutral-400">查看</span>
                      </MyBtn>
                      <MyBtn
                        $dark={true}
                        size="small"
                        className="ml-2.5 !border-2"
                        onClick={() => {
                          xhrDownload(item.artWorkUrl!)
                        }}
                      >
                        <span className="text-neutral-400">下载</span>
                      </MyBtn>
                    </span>
                  )}
                  {item?.status === SuperSizeStatus.FAIL && (
                    <MyBtn
                      $dark={true}
                      type="primary"
                      size="small"
                      className="ml-4 !border-2"
                      onClick={() =>
                        handleSuperSize(item?.multiple, item?.integral)
                      }
                    >
                      重试
                    </MyBtn>
                  )}
                </div>
              </div>
            )
          })}
      </div>
    )
  }

  useEffect(() => {
    clearTimeout(superSizeRef.current)
  }, [product.id])

  return {
    hrOpen,
    setHrOpen,
    previewVisible,
    setPreviewVisible,
    previewImage,
    setPreviewImage,
    handleSuperSize,
    updateSuperSizeInfo,
    validateSuperSize,
    HRDropdown,
  }
}
